import React, { useState } from "react";
import { getProductImage, NEGATIVE_TRANSLATIONS, NOVA_GROUP_MAPPING, NUTRIENT_TRANSLATIONS, POSITIVE_TRANSLATIONS } from "../food-constants";
import "./FoodPage.css";
import { capitalizeFirstWord, getScoreStyle } from "../constants";

const colors = {
    A: "#28a745",
    B: "#8dc63f",
    C: "#f4c20d",
    D: "#ff8c00",
    E: "#e63946",
    unknown: "#888888",
};
const getStatusColor = (status) => {
    switch (status) {
        case "high": return "#e63946";
        case "moderate": return "#f4c20d";
        case "low": return "#28a745";
        default: return "#888888";
    }
};

const getStatusText = (status) => {
    switch (status) {
        case "high": return "Yüksek";
        case "moderate": return "Orta";
        case "low": return "Düşük";
        default: return "Bilinmiyor";
    }
};

const ScoreCircle = ({ grade, label, isActive, onToggle, description }) => {
    const displayGrade = grade ? grade.replace(/\s+/g, "").toUpperCase() : "Bilinmiyor";

    return (
        <div
            className={`score-circle-container ${isActive ? "active" : ""}`}
            onClick={onToggle}
        >
            <div
                className="other-score-circle"
                style={{
                    backgroundColor: grade ? colors[grade.replace(/\s+/g, "").toUpperCase()] : colors.unknown,
                    color: "#fff",
                }}            >
                {displayGrade || "?"}
                {description && (
                    <div className="expand-icon-container">
                        <span className={`expand-icon ${isActive ? "expanded" : ""}`}></span>
                    </div>
                )}
            </div>
            <span className="score-label">{label}</span>
        </div>
    );
};

const Scores = ({ productDetails }) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const scores = [
        {
            grade: productDetails?.nutri_score_grade,
            label: "Besin Kalitesi",
            description: (
                <div className="score-detail-card nutrition-card">
                    <h3 className="score-detail-title">
                        <span className="icon">🍎</span> Besin Kalitesi
                    </h3>
                    <p className="score-detail-text">
                        <strong>Besin Kalitesi Puanı</strong> ürünlerin sağlığa etkisini değerlendirir ve
                        "A"dan "E"ye kadar sıralanır. "A" en sağlıklı, "E" ise en sağlıksız seçenektir.
                    </p>
                    <p className="score-highlight">
                        Bu ürün, <strong>"{productDetails?.nutri_score_grade?.toUpperCase()}"</strong> puanı ile
                        değerlendirilmiştir.
                    </p>

                    <div className="score-bars">
                        <div className="bar-section negative">
                            <h4 className="bar-section-title">
                                <span className="icon">⚠️</span> Negatif Bileşenler
                            </h4>
                            <div className="bar-items">
                                {productDetails?.nutri_score_details?.components?.negative?.map((comp, idx) => (
                                    <div key={idx} className="bar-item">
                                        <div className="bar-item-header">
                                            <span className="bar-item-name">{NEGATIVE_TRANSLATIONS[comp.id]?.name || comp.id}</span>
                                            <span className="bar-item-value">{comp.points} puan</span>
                                        </div>
                                        <div className="bar-container negative">
                                            <div
                                                className="bar-fill"
                                                style={{ width: `${(comp.points / 10) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bar-section positive">
                            <h4 className="bar-section-title">
                                <span className="icon">🌱</span> Pozitif Bileşenler
                            </h4>
                            <div className="bar-items">
                                {productDetails?.nutri_score_details?.components?.positive?.map((comp, idx) => (
                                    <div key={idx} className="bar-item">
                                        <div className="bar-item-header">
                                            <span className="bar-item-name">{POSITIVE_TRANSLATIONS[comp.id]?.name || comp.id}</span>
                                            <span className="bar-item-value">{comp.points} puan</span>
                                        </div>
                                        <div className="bar-container positive">
                                            <div
                                                className="bar-fill"
                                                style={{ width: `${(comp.points / 10) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            grade: NOVA_GROUP_MAPPING[productDetails?.nova_group],
            label: "İşlenmişlik",
            description: (
                <div className="score-detail-card processing-card">
                    <h3 className="score-detail-title">
                        <span className="icon">⚙️</span> İşlenmişlik Düzeyi
                    </h3>
                    <p className="score-detail-text">
                        Gıda ürünleri işlenmişlik düzeyine göre dört gruba ayrılır:
                    </p>

                    <div className="processing-levels">
                        <div className="processing-level level-a">
                            <div className="level-title">A: Az İşlenmiş</div>
                            <div className="level-desc">Doğal gıdalar</div>
                        </div>
                        <div className="processing-level level-b">
                            <div className="level-title">B: Minimum İşlenmiş</div>
                            <div className="level-desc">Basit işlemler</div>
                        </div>
                        <div className="processing-level level-c">
                            <div className="level-title">C: Orta İşlenmiş</div>
                            <div className="level-desc">Bazı katkı maddeleri</div>
                        </div>
                        <div className="processing-level level-e">
                            <div className="level-title">E: Yüksek İşlenmiş</div>
                            <div className="level-desc">Çok sayıda katkı maddesi</div>
                        </div>
                    </div>

                    <div className="processing-info">
                        <p className="processing-status">
                            Bu ürün <strong>{productDetails?.nova_group}</strong> kategorisinde yer almaktadır.
                        </p>
                        <p className="processing-additives">
                            {productDetails?.nova_group_data && `${productDetails.nova_group_data.length} katkı maddesi/bileşen içermektedir.`}
                        </p>
                    </div>

                    <div className="additive-tags">
                        {productDetails?.nova_group_data?.slice(0, 8).map((item, idx) => (
                            <span key={idx} className="additive-tag">
                                {item.type === "Additives" ? item.code : (item.tr_name || item.name)}
                            </span>
                        ))}
                        {productDetails?.nova_group_data?.length > 8 && (
                            <span className="additive-tag more-tag">
                                +{productDetails.nova_group_data.length - 8} daha
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            grade: productDetails?.eco_score_grade,
            label: "Çevresel Etki",
            description: (
                <div className="score-detail-card eco-card">
                    <h3 className="score-detail-title">
                        <span className="icon">🌍</span> Çevresel Etki
                    </h3>
                    <p className="score-detail-text">
                        Çevresel etki puanı, ürünün çevre üzerindeki etkisini değerlendirir.
                        "A"dan "E"ye kadar sıralanır. "A" en düşük etkiyi, "E" ise en yüksek etkiyi gösterir.
                    </p>

                    <div className="eco-info">
                        <p className="eco-status">
                            Bu ürün <strong>{productDetails?.eco_score_grade?.toUpperCase() || "?"}</strong> puanı ile değerlendirilmiştir.
                        </p>
                    </div>

                    {productDetails?.eco_score_details && (
                        <div className="eco-details">
                            <div className="eco-detail-item">
                                <span className="eco-icon">♻️</span>
                                <p>Ambalaj geri dönüştürülebilir.</p>
                            </div>
                            <div className="eco-detail-item">
                                <span className="eco-icon">💧</span>
                                <p>Üretim süreci su tasarrufu sağlar.</p>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="scores-section">
            <div className="score-circles">
                {scores.map((score, index) => (
                    <ScoreCircle
                        key={index}
                        grade={score.grade}
                        label={score.label}
                        isActive={activeIndex === index}
                        onToggle={() => setActiveIndex(activeIndex === index ? null : index)}
                        description={score.description}
                    />
                ))}
            </div>

            {activeIndex !== null && (
                <div className="score-details">
                    <div className="score-details-connector"></div>
                    <div>
                        {scores[activeIndex].description}
                    </div>
                </div>
            )}
        </div>
    );
};

const NutrientBar = ({ nutrient, percentage, status = "unknown" }) => {
    const formattedPercentage = percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);

    return (
        <div className="nutrient-bar">
            <div className="nutrient-bar-header">
                <span className="bar-nutrient-name">{NUTRIENT_TRANSLATIONS[nutrient] || nutrient}</span>
                <div className="nutrient-values">
                    <span className="nutrient-percentage">{formattedPercentage}%</span>
                    <span
                        className="nutrient-status"
                        style={{
                            backgroundColor: `${getStatusColor(status)}20`,
                            color: getStatusColor(status)
                        }}
                    >
                        {getStatusText(status)}
                    </span>
                </div>
            </div>
            <div
                className="nutrient-progress"
                style={{ backgroundColor: `${getStatusColor(status)}20` }}
            >
                <div
                    className="nutrient-progress-fill"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: getStatusColor(status)
                    }}
                ></div>
            </div>
        </div>
    );
};

const AllergyCard = ({ allergens }) => {
    const allergenList = typeof allergens === "string"
        ? allergens.split(",").map((allergen) => allergen.trim())
        : [];

    return (
        <div className="allergy-card">
            <div className="allergen-tags">
                {allergenList.length > 0 ? (
                    allergenList.map((allergen, index) => (
                        <span key={index} className="allergen-tag">
                            {allergen.charAt(0).toUpperCase() + allergen.slice(1).toLowerCase()}
                        </span>
                    ))
                ) : (
                    <span className="allergen-tag allergen-free">
                        Alerjen bilgisi bulunmamaktadır
                    </span>
                )}
            </div>
        </div>
    );
};

const GoodBadList = ({ badItems, positiveComponents }) => {
    const [selectedNegativeIndex, setSelectedNegativeIndex] = useState(null);
    const [selectedPositiveIndex, setSelectedPositiveIndex] = useState(null);

    const allPositiveItems = positiveComponents?.map((item) => ({
        id: item.id,
        name: POSITIVE_TRANSLATIONS[item.id]?.name || item.id,
        description: POSITIVE_TRANSLATIONS[item.id]?.description || "",
        points: item.points,
        value: item.value,
        unit: item.unit,
    }));

    return (
        <div className="feature-cards">
            <div className="feature-card positive-features">
                <h3 className="feature-card-title">
                    <span className="icon">🌱</span> Olumlu Özellikler
                </h3>

                <div className="feature-tags">
                    {allPositiveItems?.length > 0 ? (
                        allPositiveItems.map((item, idx) =>
                            item.points > 0 ? (
                                <div key={idx} className="feature-tag-container">
                                    <span
                                        className={`feature-tag positive-tag ${selectedPositiveIndex === idx ? "active" : ""}`}
                                        onClick={() => {
                                            setSelectedPositiveIndex(selectedPositiveIndex === idx ? null : idx);
                                            setSelectedNegativeIndex(null);
                                        }}
                                    >
                                        {capitalizeFirstWord(item.name)} ({item.value} {item.unit})
                                    </span>

                                    {selectedPositiveIndex === idx && item.description && (
                                        <div className="description-box">
                                            {item.description}
                                        </div>
                                    )}
                                </div>
                            ) : null
                        )
                    ) : (
                        <span className="feature-tag empty-tag">
                            Bilgi yok
                        </span>
                    )}
                </div>
            </div>

            <div className="feature-card negative-features">
                <h3 className="feature-card-title">
                    <span className="icon">⚠️</span> Olumsuz Özellikler
                </h3>

                <div className="feature-tags">
                    {badItems?.length > 0 ? (
                        badItems.map((item, index) => (
                            <div key={index} className="feature-tag-container">
                                <span
                                    className={`feature-tag negative-tag ${selectedNegativeIndex === index ? "active" : ""}`}
                                    onClick={() => {
                                        setSelectedNegativeIndex(selectedNegativeIndex === index ? null : index);
                                        setSelectedPositiveIndex(null);
                                    }}
                                >
                                    {item.tr_name} {item.percentage !== 0 && `${item.percentage}%`}
                                </span>

                                {selectedNegativeIndex === index && item.description && (
                                    <div className="description-box">
                                        {item.description}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <span className="feature-tag empty-tag">
                            Bilgi yok
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const NutrientServing = ({ serving, portion }) => {
    if (!serving || Object.keys(serving).length === 0) {
        return (
            <div className="empty-state">
                <p>Besin bilgisi bulunamadı.</p>
            </div>
        );
    }

    // Default daily intake values (example)
    const defaultDailyIntake = {
        carbohydrates: 300,
        fat: 70,
        proteins: 50,
        fiber: 25
    };

    const nutrientIcons = {
        carbohydrates: "🍞",
        fat: "🧈",
        proteins: "🥩",
        fiber: "🌾"
    };

    const nutrientNames = {
        carbohydrates: "Karbonhidrat",
        fat: "Yağ",
        proteins: "Protein",
        fiber: "Lif"
    };

    const getStatus = (value, nutrient) => {
        const dailyValue = defaultDailyIntake[nutrient] || 1;
        const percentage = (value / dailyValue) * 100;

        if (percentage > 100) return "high";
        if (percentage > 70) return "moderate";
        return "low";
    };

    return (
        <div className="nutrient-serving">
            <div className="nutrient-items">
                {["carbohydrates", "fat", "proteins", "fiber"].map(nutrient => {
                    const value = parseFloat(serving[nutrient] || 0);
                    const percentage = (value / (defaultDailyIntake[nutrient] || 1)) * 100;
                    const status = getStatus(value, nutrient);

                    return (
                        <div key={nutrient} className="nutrient-item">
                            <div className="nutrient-icon">
                                {nutrientIcons[nutrient]}
                            </div>

                            <div className="nutrient-content">
                                <div className="nutrient-header">
                                    <span className="nutrient-title">{nutrientNames[nutrient]}</span>
                                    <span className="nutrient-value">{value} gr</span>
                                </div>

                                <div className="nutrient-progress-container">
                                    <div
                                        className="nutrient-progress"
                                        style={{ backgroundColor: `${getStatusColor(status)}20` }}
                                    >
                                        <div
                                            className="nutrient-progress-fill"
                                            style={{
                                                width: `${percentage}%`,
                                                backgroundColor: getStatusColor(status)
                                            }}
                                        ></div>
                                    </div>

                                    <div className="nutrient-status-info">
                                        <span
                                            className="nutrient-level"
                                            style={{ color: getStatusColor(status) }}
                                        >
                                            {getStatusText(status)}
                                        </span>
                                        <span className="nutrient-daily">
                                            Günlük ihtiyacın %{percentage.toFixed(0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const IngredientsList = ({ ingredients }) => {
    if (!ingredients || ingredients.length === 0) {
        return (
            <div className="empty-state">
                <p>İçerik bilgisi bulunamadı.</p>
            </div>
        );
    }

    // Generate colors for pie chart segments
    const generateColors = (count) => {
        const colors = [
            "#4CAF50", "#8BC34A", "#CDDC39", "#FFEB3B",
            "#FFC107", "#FF9800", "#FF5722", "#F44336",
            "#E91E63", "#9C27B0", "#673AB7", "#3F51B5"
        ];

        return Array(count).fill().map((_, i) => colors[i % colors.length]);
    };

    const colors = generateColors(ingredients.length);

    // Create a simple pie chart with CSS conic gradient
    const createPieChart = () => {
        let cumulativePercentage = 0;
        const segments = ingredients.map((item, index) => {
            const startPercentage = cumulativePercentage;
            const percentage = parseFloat(item.perc);
            cumulativePercentage += percentage;

            return {
                color: colors[index],
                start: startPercentage,
                end: cumulativePercentage
            };
        });

        const gradientParts = segments.map(segment =>
            `${segment.color} ${segment.start}% ${segment.end}%`
        ).join(', ');

        return `conic-gradient(${gradientParts})`;
    };

    return (
        <div className="ingredients-section">
            <div className="ingredients-container">
                <div className="ingredients-list">
                    {ingredients.map((item, index) => (
                        <div key={index} className="ingredient-item">
                            <div
                                className="ingredient-color"
                                style={{ backgroundColor: colors[index] }}
                            ></div>
                            <div className="ingredient-info">
                                <span className="ingredient-name">{item.tr}</span>
                                <span className="ingredient-percentage">%{item.perc}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="pie-chart-container">
                    <div
                        className="pie-chart"
                        style={{ background: createPieChart() }}
                    >
                        <div className="pie-chart-center"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TagsSection = ({ labels }) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const tags = [
        { key: "gluten", label: "Gluten", color: "#FF3B30", icon: "🌾" },
        { key: "lactose", label: "Laktoz", color: "#FFC107", icon: "🥛" },
        { key: "vegan", label: "Vegan", color: "#388E3C", icon: "🌱" },
        { key: "vegetarian", label: "Vejetaryan", color: "#FF9800", icon: "🥗" },
        { key: "palm_oil", label: "Palm Yağı", color: "#6D4C41", icon: "🌴" },
        { key: "halal", label: "Helal", color: "#43A047", icon: "✅" },
    ];

    const renderTag = (tag, index, rowIndex) => {
        const { key, label, color, icon } = tag;
        const isPositive = labels && labels[key]?.status === true;
        const reasons = labels && labels[key]?.reasons_tr || [];
        const isActive = activeIndex === rowIndex * 10 + index;
        const isContextual = key === "vegan" || key === "vegetarian" || key === "halal";

        // Determine the label text based on the tag type and status
        let finalLabel;
        if (key === "gluten") {
            finalLabel = !isPositive ? `${label} İçerir` : `${label} İçermez`;
        } else if (isContextual) {
            finalLabel = isPositive ? label : `${label} Değildir`;
        } else {
            finalLabel = isPositive ? `${label} İçerir` : `${label} İçermez`;
        }

        // Determine if we should show a cross (negative indicator)
        const showCross = (() => {
            if (key === "gluten") return !isPositive;
            if (isContextual) return !isPositive;
            return isPositive;
        })();

        return (
            <div key={key} className="tag-item">
                <div
                    className={`tag-circle ${isActive ? "active" : ""}`}
                    style={{
                        backgroundColor: `${color}20`,
                        color: color
                    }}
                    onClick={() => reasons.length > 0 && setActiveIndex(isActive ? null : rowIndex * 10 + index)}
                >
                    <span className="tag-icon">{icon}</span>

                    {showCross && (
                        <div className="tag-cross">
                            <div className="cross-line"></div>
                        </div>
                    )}

                    {reasons.length > 0 && (
                        <div className="tag-expand">
                            <span className={`expand-icon ${isActive ? "expanded" : ""}`}></span>
                        </div>
                    )}
                </div>
                <span className="tag-label">{finalLabel}</span>
            </div>
        );
    };

    const renderTagRow = (tagGroup, rowIndex) => (
        <>
            <div className="tag-row">
                {tagGroup.map((tag, index) => renderTag(tag, index, rowIndex))}
            </div>

            {tagGroup.map((tag, index) => {
                const { key, label } = tag;
                const isActive = activeIndex === rowIndex * 10 + index;
                const reasons = labels && labels[key]?.reasons_tr?.map(
                    (reason) => reason.charAt(0).toUpperCase() + reason.slice(1)
                ) || [];
                const isContextual = key === "vegan" || key === "vegetarian" || key === "halal";

                if (isActive && reasons.length > 0) {
                    return (
                        <div key={`${key}-details`} className="tag-details">
                            <div className="tag-details-connector"></div>
                            <div className="tag-details-content">
                                <p className="tag-details-title">
                                    Bu ürün {label}{" "}
                                    {!isContextual
                                        ? "içerir çünkü içerisinde aşağıdaki ürünler bulunur:"
                                        : "değildir çünkü içerisinde aşağıdaki ürünler bulunur:"}
                                </p>
                                <ul className="tag-details-list">
                                    {reasons.map((reason, idx) => (
                                        <li key={idx} className="tag-details-item">
                                            <span className="tag-details-bullet"></span>
                                            <span>{reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    );
                }
                return null;
            })}
        </>
    );

    const firstRowTags = tags.slice(0, 3);
    const secondRowTags = tags.slice(3);

    return (
        <>
            {renderTagRow(firstRowTags, 0)}
            {renderTagRow(secondRowTags, 1)}
        </>
    );
};

const FoodPage = ({ product }) => {
    const [activeTab, setActiveTab] = useState("genel");

    if (!product) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Ürün bilgileri yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className="hair-page">
            <div className="product-card">
                <div className="image-container">
                    <img
                        src={getProductImage("food", product.barcode)}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = product.image;
                        }}
                        alt={product.name}
                        className="product-image"
                    />
                    <div className="score-circle" style={getScoreStyle(product.total_score)}>
                        <span className="score-big">{product.total_score}</span>
                        <span className="score-small">/100</span>
                    </div>
                </div>
                <div className="text-content">
                    <h1 className="product-name">{product.name}</h1>
                    <h2 className="brand">{product.brand}</h2>
                </div>
            </div>

            <div className="tabs-container">
                <button
                    className={`tab-button ${activeTab === "genel" ? "active" : ""}`}
                    onClick={() => setActiveTab("genel")}
                >
                    Genel
                </button>
                <button
                    className={`tab-button ${activeTab === "besin" ? "active" : ""}`}
                    onClick={() => setActiveTab("besin")}
                >
                    Besin Değerleri
                </button>
                <button
                    className={`tab-button ${activeTab === "icerik" ? "active" : ""}`}
                    onClick={() => setActiveTab("icerik")}
                >
                    İçindekiler
                </button>
                <button
                    className={`tab-button ${activeTab === "etiket" ? "active" : ""}`}
                    onClick={() => setActiveTab("etiket")}
                >
                    Ürün Özellikleri
                </button>
            </div>

            <div className="tab-content">
                {activeTab === "genel" && (
                    <>
                        <div className="section">
                            <Scores productDetails={product.product_details[0]} />
                        </div>
                        <div className="section">
                            <GoodBadList
                                badItems={product.product_details[0].nova_additives}
                                positiveComponents={product.product_details[0].nutri_score_details.components.positive}
                            />
                        </div>
                    </>
                )}

                {activeTab === "besin" && (
                    <div className="section">
                        <h2>{product.product_details[0].serving_quantity ? `Porsiyon Başı (${product.product_details[0].serving_quantity}) Besin Değerleri` : "Besin Değerleri"}</h2>
                        <NutrientServing
                            serving={product.product_details[0].nutrient_info.serving}
                            portion={product.product_details[0].serving_quantity}
                        />
                    </div>
                )}

                {activeTab === "icerik" && (
                    <>
                        <div className="section">
                            <h2><span className="icon">ℹ️</span> İçindekiler</h2>
                            <IngredientsList ingredients={product.product_details[0].ingredients_summary} />
                        </div>
                        <div className="section">
                            <h2><span className="icon">⚠️</span> Alerjenler</h2>
                            <AllergyCard allergens={product.product_details[0].allergen_alerts} />
                        </div>
                        <div className="section">
                            <h2><span className="icon">📊</span> Besin Seviyeleri</h2>
                            <div className="nutrient-levels">
                                <div className="nutrient-bars">
                                    {product?.product_details && product?.product_details[0].nutrient_levels &&
                                        product?.product_details[0].nutrient_levels
                                            .filter((nutrient) => nutrient.perc && nutrient.name)
                                            .sort((a, b) => parseFloat(b.perc) - parseFloat(a.perc))
                                            .map((nutrient, index) => (
                                                <NutrientBar
                                                    key={index}
                                                    nutrient={nutrient.name}
                                                    percentage={parseFloat(nutrient.perc)}
                                                    status={nutrient.status || "unknown"}
                                                />
                                            ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === "etiket" && (
                    <div className="section">
                        <TagsSection labels={product.product_details[0].labels_tags} />
                    </div>
                )}
            </div>
        </div >
    );
};

export default FoodPage;
